import React, { useState, useEffect } from 'react';
import { KanbanSquare, StickyNote, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { saasApi } from '../../api/client';
import { useToast } from '../../hooks/use-toast';

const STAGES = [
  { id: 'novo', label: 'Novo contato', color: 'bg-blue-100 text-blue-800' },
  { id: 'tentativa_contato', label: 'Tentativa de contato', color: 'bg-amber-100 text-amber-800' },
  { id: 'conversa_agendada', label: 'Conversa agendada', color: 'bg-purple-100 text-purple-800' },
  { id: 'em_avaliacao', label: 'Em avaliação', color: 'bg-emerald-100 text-emerald-800' },
  { id: 'encerrado', label: 'Encerrado', color: 'bg-[#f2f6f7] text-[#425a62]' },
];

export const CrmTab = () => {
  const [contacts, setContacts] = useState(null);
  const [noteInputs, setNoteInputs] = useState({});
  const { toast } = useToast();

  const load = () => saasApi.crmContacts().then(setContacts).catch(() => setContacts([]));
  useEffect(() => { load(); }, []);

  if (contacts === null) return <div className="text-[#52676e] text-sm py-8 text-center">Carregando CRM...</div>;

  const setStage = async (id, stage) => {
    const updated = await saasApi.crmUpdate(id, { stage });
    setContacts(cs => cs.map(c => c.id === id ? updated : c));
    toast({ title: 'Etapa atualizada' });
  };

  const addNote = async (id) => {
    const note = (noteInputs[id] || '').trim();
    if (!note) return;
    const updated = await saasApi.crmUpdate(id, { note });
    setContacts(cs => cs.map(c => c.id === id ? updated : c));
    setNoteInputs(n => ({ ...n, [id]: '' }));
  };

  if (contacts.length === 0) return (
    <div className="bg-white border border-[#cbd8dc] rounded-[10px] p-10 text-center" data-testid="crm-empty">
      <KanbanSquare className="w-10 h-10 text-[#6b858e] mx-auto mb-3" />
      <h3 className="font-semibold text-[#153e44]">Nenhum contato no CRM</h3>
      <p className="text-sm text-[#52676e] mt-1">Contatos criados pela recepcionista digital (com consentimento) entram aqui automaticamente.</p>
    </div>
  );

  return (
    <div className="space-y-3" data-testid="crm-list">
      <div className="flex flex-wrap gap-2 text-xs">
        {STAGES.map(s => {
          const count = contacts.filter(c => c.stage === s.id).length;
          return <span key={s.id} className={`px-2.5 py-1 rounded-full font-medium ${s.color}`}>{s.label}: {count}</span>;
        })}
      </div>
      {contacts.map(c => {
        const stage = STAGES.find(s => s.id === c.stage) || STAGES[0];
        return (
          <div key={c.id} className="bg-white border border-[#cbd8dc] rounded-[10px] p-5" data-testid={`crm-contact-${c.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-semibold text-[#153e44]">{c.name} <span className="text-xs font-normal text-[#52676e]">• {c.category} • {c.city}</span></div>
                <div className="text-xs text-[#52676e] mt-0.5">{c.contact} ({c.channel}) • Origem: {c.source === 'recepcionista_ia' ? 'Recepcionista IA' : c.source}</div>
              </div>
              <Select value={c.stage} onValueChange={v => setStage(c.id, v)}>
                <SelectTrigger className={`w-48 h-9 text-xs font-medium ${stage.color} border-0`} data-testid={`crm-stage-select-${c.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>{STAGES.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {c.summary && <p className="text-xs text-[#425a62] mt-2 line-clamp-2">{c.summary}</p>}
            {(c.notes || []).length > 0 && (
              <div className="mt-3 space-y-1.5">
                {c.notes.map((n, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs text-[#425a62] bg-[#f7fafb] rounded-[7px] px-3 py-2">
                    <StickyNote className="w-3 h-3 text-[#075a6d] mt-0.5 shrink-0" />
                    <span>{n.text} <span className="text-[#6b858e]">— {new Date(n.at).toLocaleString('pt-BR')}</span></span>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2 mt-3">
              <Input value={noteInputs[c.id] || ''} onChange={e => setNoteInputs(n => ({ ...n, [c.id]: e.target.value }))} placeholder="Adicionar nota..." className="h-9 text-sm" data-testid={`crm-note-input-${c.id}`} />
              <Button size="sm" variant="outline" onClick={() => addNote(c.id)} data-testid={`crm-note-btn-${c.id}`}><Plus className="w-4 h-4" /></Button>
            </div>
          </div>
        );
      })}
    </div>
  );
};
